// src/pages/admin/AdminInquiryPage.jsx
import { useEffect, useState, useMemo } from "react";
import styles from "./AdminSupport.module.css";
import { useAuth } from "../../context/AuthContext";

const PAGE_SIZE = 10;

const STATUS_OPTIONS = [
  { value: "", label: "전체" },
  { value: "OPEN", label: "OPEN" },
  { value: "ANSWERED", label: "ANSWERED" },
  { value: "CLOSED", label: "CLOSED" },
];

export default function AdminInquiryPage() {
  const { user } = useAuth(); // SYSTEM 여부 판단용
  const isSystem = user?.role === "SYSTEM";

  const [status, setStatus] = useState("");          // 필터 상태
  const [tenantKeyFilter, setTenantKeyFilter] = useState(""); // SYSTEM일 때만 의미

  const [page, setPage] = useState(0);
  const [listData, setListData] = useState({
    content: [],
    totalPages: 0,
    totalElements: 0,
    number: 0,
  });
  const [loadingList, setLoadingList] = useState(false);

  const [selected, setSelected] = useState(null); // { inquiry, messages }
  const [replyContent, setReplyContent] = useState("");
  const [changingStatus, setChangingStatus] = useState(false);
  const [searchField, setSearchField] = useState("title"); // "title" | "user"
  const [searchText, setSearchText] = useState("");

  const buildListUrl = () => {
    const params = new URLSearchParams();
    params.set("page", page);
    params.set("size", PAGE_SIZE);
    if (status) params.set("status", status);
    if (isSystem && tenantKeyFilter) params.set("tenantKey", tenantKeyFilter);
    return `/api/support/admin/inquiries?${params.toString()}`;
  };

  const loadList = () => {
    setLoadingList(true);
    fetch(buildListUrl(), { credentials: "include" })
      .then((res) => res.json())
      .then((res) => setListData(res))
      .finally(() => setLoadingList(false));
  };

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, status, tenantKeyFilter]);

  const handleRowClick = (id) => {
    fetch(`/api/support/admin/inquiries/${id}`, { credentials: "include" })
      .then((res) => res.json())
      .then((res) => {
        setSelected(res);
        setReplyContent("");
      });
  };

  const handleReplySubmit = async (e) => {
    e.preventDefault();
    if (!selected) return;
    if (!replyContent.trim()) return;

    const id = selected.inquiry.id;
    const res = await fetch(`/api/support/inquiries/${id}/reply`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ content: replyContent }),
    });
    if (res.ok) {
      // 다시 상세 로드
      handleRowClick(id);
      setReplyContent("");
      // 리스트도 갱신
      loadList();
    } else {
      alert("답변 등록에 실패했습니다.");
    }
  };

  // ✅ 행(row)에서 상태 변경
  const handleStatusChange = async (id, newStatus) => {
    setChangingStatus(true);
    const res = await fetch(`/api/support/admin/inquiries/${id}/status`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ status: newStatus }),
    });
    setChangingStatus(false);
    if (res.ok) {
      // 리스트 갱신
      loadList();
      // 현재 선택된 문의라면 상세도 갱신
      if (selected?.inquiry?.id === id) {
        handleRowClick(id);
      }
    } else {
      alert("상태 변경에 실패했습니다.");
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    if (!window.confirm("이 문의와 모든 메시지를 삭제하시겠습니까?")) return;
    const id = selected.inquiry.id;
    const res = await fetch(`/api/support/admin/inquiries/${id}`, {
      method: "DELETE",
      credentials: "include",
    });
    if (res.ok) {
      setSelected(null);
      loadList();
    } else {
      alert("삭제에 실패했습니다.");
    }
  };

  const filteredContent = useMemo(() => {
    const term = searchText.trim().toLowerCase();
    if (!term) return listData.content;

    return listData.content.filter((q) => {
      if (searchField === "title") {
        return q.title?.toLowerCase().includes(term);
      } else {
        // 사용자 검색: 회원이면 userId, 게스트면 guestName 기준
        if (q.userId === 0) {
          const guestName = (q.guestName || "guest").toLowerCase();
          return guestName.includes(term);
        }
        return String(q.userId).toLowerCase().includes(term);
      }
    });
  }, [listData.content, searchField, searchText]);

  return (
    <main className={styles.page}>
      <h1 className={styles.pageTitle}>1:1 문의 관리</h1>

      <div className={styles.split}>
        {/* 문의 리스트 */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2>문의 목록</h2>

            {/* 🔎 상태 + 검색 컨트롤 */}
            <div className={styles.listControls}>
              <div className={styles.filterInline}>
                <span className={styles.filterLabel}>상태</span>
                <select
                  value={status}
                  onChange={(e) => {
                    setPage(0);
                    setStatus(e.target.value);
                  }}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.searchGroup}>
                <select
                  value={searchField}
                  onChange={(e) => setSearchField(e.target.value)}
                >
                  <option value="title">제목</option>
                  <option value="user">사용자</option>
                </select>
                <input
                  type="search"
                  placeholder="검색어 입력"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") setSearchText("");
                  }}
                />
              </div>
            </div>
          </div>

          {loadingList ? (
            <p>로딩 중...</p>
          ) : (
            <>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th style={{ width: "60px" }}>ID</th>
                    <th style={{ textAlign: "left"}}>제목</th>
                    <th style={{ width: "120px" }}>상태</th>
                    <th style={{ width: "100px" }}>사용자</th>
                    <th style={{ width: "160px" }}>작성일</th>
                    <th style={{ width: "140px" }}>상태 관리</th>
                  </tr>
                </thead>
                <tbody>
                  {listData.content.map((q) => (
                    <tr
                      key={q.id}
                      className={
                        selected?.inquiry?.id === q.id ? styles.rowSelected : ""
                      }
                      onClick={() => handleRowClick(q.id)}
                    >
                      <td>{q.id}</td>
                      <td style={{ textAlign: "left"}}>{q.title}</td>
                      <td>{q.status}</td>
                      <td>
                        {q.userId === 0 
                          ? (q.guestName || <span style={{ color: "#9ca3af" }}>GUEST</span>)
                          : q.userId}
                      </td>

                      <td>
                        {q.createdAt?.slice(0, 16).replace("T", " ")}
                      </td>
                      <td
                        onClick={(e) => e.stopPropagation()} // ✅ 행 클릭과 분리
                      >
                        <select
                          className={styles.statusSelect}
                          value={q.status}
                          disabled={changingStatus}
                          onChange={(e) =>
                            handleStatusChange(q.id, e.target.value)
                          }
                        >
                          {STATUS_OPTIONS.filter((s) => s.value).map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className={styles.pagination}>
                <button
                  disabled={page === 0}
                  onClick={() => setPage((p) => p - 1)}
                >
                  이전
                </button>
                <span>
                  {page + 1} / {listData.totalPages || 1}
                </span>
                <button
                  disabled={page + 1 >= listData.totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  다음
                </button>
              </div>
            </>
          )}
        </section>

        {/* 상세 + 답변 영역 */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2>문의 상세</h2>
          </div>

          {!selected ? (
            <p className={styles.empty}>왼쪽에서 문의를 선택하세요.</p>
          ) : (
            <>
              <div className={styles.inquiryHeader}>
                <div>
                  <div className={styles.inquiryTitle}>
                    [{selected.inquiry.status}] {selected.inquiry.title}
                  </div>
                  <div className={styles.inquiryMeta}>
                    <span>사용자: {selected.inquiry.userId === 0 
                          ? (`${selected.inquiry.guestName} (${selected.inquiry.guestEmail})`)
                          : selected.inquiry.userId}
                    </span>
                    <span>
                      작성일:{" "}
                      {selected.inquiry.createdAt
                        ?.slice(0, 16)
                        .replace("T", " ")}
                    </span>
                  </div>
                </div>
              </div>

              {/* 메시지 스레드 */}
              <div className={styles.thread}>
                {selected.messages.map((m) => (
                  <div
                    key={m.id}
                    className={
                      m.senderRole === "ADMIN"
                        ? `${styles.msg} ${styles.admin}`
                        : `${styles.msg} ${styles.userMsg}`
                    }
                  >
                    <div className={styles.msgMeta}>
                      <span>{m.senderRole === "ADMIN" ? "관리자" : "사용자"}</span>
                      <span>
                        {m.createdAt?.slice(0, 16).replace("T", " ")}
                      </span>
                    </div>
                    <div className={styles.msgBody}>
                      {m.content.split("\n").map((line, idx) => (
                        <p key={idx}>{line}</p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* 답변 입력 */}
              <form className={styles.replyForm} onSubmit={handleReplySubmit}>
                <label>
                  답변 내용
                  <textarea
                    rows={4}
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    placeholder="사용자에게 보낼 답변을 입력하세요."
                  />
                </label>

                <div className={styles.actions}>
                  <button
                    type="button"
                    className={styles.smallBtnDanger}
                    onClick={handleDelete}
                  >
                    삭제
                  </button>
                  <button type="submit">답변 등록</button>
                </div>
              </form>
            </>
          )}
        </section>
      </div>
    </main>
  );
}
